import React, { useCallback, useEffect, useState, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { X } from "lucide-react";
import { EntityType } from "@repo/types/enums";
import type { SearchParams } from "../../types/ui/views";
import type {
  Entity,
  RecordId,
  RecordProps,
  TableGridProps,
} from "@repo/types/ui/tables";
import { useGetDataViews } from "../../hooks/queries";
import { useGetEntityById as useGetEntityById } from "../../hooks/queries/useEntities";
import PageHeading from "./PageHeading";
import AddDataView from "../dataViews/AddDataView";
import { DataViewFilters } from "../dataViews/DataViewFilters";
import { TableDataView } from "../tables";

export interface PageViewProps<T> extends TableGridProps<T> {
  useColumns: (props: RecordProps) => ColumnDef<T>[];
  renderRecord: (recordId: RecordId) => React.ReactNode;
  renderNewRecord: () => React.ReactNode;
}

const PageView = <T extends Entity>({
  entityType,
  useColumns,
  onCellValueUpdated,
  renderRecord,
  renderNewRecord,
}: PageViewProps<T>) => {
  //const { mainContentRef } = useLayout();
  const { data: dataViews } = useGetDataViews(entityType);

  const [activeViewId, setActiveViewId] = useState<number | null>(null);
  const [selectedRecordId, setSelectedRecordId] = useState<RecordId>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    source: null,
    keywords: "",
    filters: {},
    inProgress: {
      keywords: false,
      filters: false,
    },
  });

  const { data: entity } = useGetEntityById(entityType, selectedRecordId);

  const handleViewChange = (viewId: number) => {
    setActiveViewId(viewId);
  };

  const handleOpenRecord = useCallback((recordId: RecordId) => {
    setSelectedRecordId(recordId);
  }, []);

  //const handleUpdateRecord = useCallback((queryClient: QueryClient) => {}, []);

  const scrollCallbacks = useRef<((scrollLeft: number) => void)[]>([]);
  const registerHorizontalScroll = (cb: (scrollLeft: number) => void) => {
    scrollCallbacks.current.push(cb);

    return () => {
      scrollCallbacks.current = scrollCallbacks.current.filter(
        (fn) => fn !== cb,
      );
    };
  };

  const handleCloseRecord = () => setSelectedRecordId(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  const handleChangeSearchParams = useCallback((params: SearchParams) => {
    setSearchParams(params);
  }, []);

  const columns = useColumns({
    onOpenRecord: handleOpenRecord,
    //onUpdateRecord: handleUpdateRecord,
  });

  useEffect(() => {
    if (dataViews && dataViews.length > 0 && activeViewId === null) {
      setActiveViewId(dataViews[0].Id);
    }
  }, [dataViews, activeViewId]);

  const handleScroll = () => {
    const scrollLeft = scrollRef.current?.scrollLeft || 0;

    scrollCallbacks.current.forEach((cb) => cb(scrollLeft));
  };

  return (
    <>
      <div className="h-[calc(100vh-55px)] flex flex-col overflow-hidden bg-[#ffffff]">
        <div className="shrink-0 bg-white px-4 py-2 z-50">
          <PageHeading heading={EntityType[entityType]}>
            <AddDataView entityType={entityType} />
          </PageHeading>

          <div className="flex border-b border-gray-200">
            {!dataViews
              ? Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton
                    key={index}
                    width={100}
                    height={30}
                    className="mr-4"
                  />
                ))
              : dataViews.map((dataView) => (
                  <div
                    key={dataView.Id}
                    onClick={() => handleViewChange(dataView.Id)}
                    className={`relative pr-4 pl-4 pt-2 pb-3 rounded-t-xs text-sm outline-none transition hover:bg-gray-200 hover:cursor-pointer
                    ${activeViewId === dataView.Id ? "border-b-2 border-sky-600" : ""}`}
                  >
                    {dataView.Name}
                  </div>
                ))}
          </div>

          <div className="mt-5 mb-5">
            <DataViewFilters
              searchParams={searchParams}
              onSearchParamsChange={handleChangeSearchParams}
              renderNewRecord={renderNewRecord}
            />
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-auto relative pl-0 pr-0 table-scroll-container"
        >
          <div className="w-max min-w-full">
            <TableDataView<T>
              key={activeViewId}
              viewId={activeViewId}
              entityType={entityType}
              onHorizontalScroll={registerHorizontalScroll}
              columns={columns}
              searchParams={searchParams}
              onSearchParamsChange={handleChangeSearchParams}
              onCellValueUpdated={onCellValueUpdated}
            />
          </div>
        </div>

        <Dialog open={selectedRecordId != null} onClose={handleCloseRecord}>
          <div
            className={
              "fixed inset-0 flex items-end justify-end p-6 right-7 bottom-3"
            }
          >
            <DialogBackdrop className="fixed inset-0 bg-black/60" />

            <DialogPanel className="fixed inset-8 flex items-center justify-center rounded-2xl">
              <div className="grid grid-rows-[auto_1fr] w-full h-full rounded-2xl shadow-lg overflow-hidden bg-white border-gray-50">
                <DialogTitle className="flex items-center justify-between px-4 py-4 bg-white text-2xl font-medium">
                  <strong>{entity ? entity.Title : <Skeleton />}</strong>
                  <X
                    className="h-6 w-6 text-gray-500 hover:cursor-pointer"
                    onClick={handleCloseRecord}
                  />
                </DialogTitle>

                {renderRecord(selectedRecordId!)}
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      </div>
    </>
  );
};

export { PageView };
